/*
 * This component is for requesting conversations from slack and creating a pretty dropdown
 * selector with slack's response.
 *
 * It does *not* display a connect slack option when there's no slack connection that exists:
 * use something else for that. It also uses no pre-existing window variables (it does store
 * the channels in the window so they're only requested once I guess).
 */

import React, { Fragment } from 'react';
import Select from 'react-select';

const isChannel = conversation => !!conversation.is_channel && !conversation.is_private;
const getChannelTitle = (conversation) => `#${conversation.name}`;

const isPrivateChannel = conversation => conversation.is_private && !conversation.is_mpim;
const getPrivateChannelTitle = conversation => `🔒${conversation.name}`;

const isDMChannel = conversation => !conversation.is_channel && !conversation.is_group;
const getDMChannelTitle = conversation => `@${conversation.slack_name}`;

const isGroupDMChannel = conversation => conversation.is_group && conversation.is_mpim;
const getGroupDMChannelTitle = conversation => conversation.name
    .split('-')
    .slice(1, -1)
    .reduce((names, name) => {
        if (name) names.push(`@${name}`);
        return names;
    }, [])
    .join(', ');

const CONVERSATION_METHODS = {
    Channels: {
        test: isChannel,
        getConversationTitle: getChannelTitle,
    },
    'Private Channels': {
        test: isPrivateChannel,
        getConversationTitle: getPrivateChannelTitle,
    },
    'Direct Messages': {
        test: isDMChannel,
        getConversationTitle: getDMChannelTitle,
    },
    'Group Messages': {
        test: isGroupDMChannel,
        getConversationTitle: getGroupDMChannelTitle,
    },
};

class SlackConversationSelector extends React.Component {
    constructor(props) {
        super(props);
        this.state = this.getState(props);
    }

    getState = (props) => {
        const slackConversations = this.getSlackConversations();
        const loading = !slackConversations;

        return {
            loading,
            slackConversations,
            selectedConversations: [],
            slackMessage: '',
            errorMessage: '',
        };
    };

    getSlackConversations = () => {
        let url = `/api/teams/${window.teamId}/users/${window.userId}/integrations/slack-conversations`;
        $.get(url)
            .then(({ conversations }) => {
                const slackConversations = Object.values(conversations);
                const loading = false;
                let selectedConversations;

                if (this.props.initialConversationIds) {
                    const convoIdMatches = (element) => this.props.initialConversationIds.includes(element.id);
                    const initialConversations = slackConversations.filter(convoIdMatches);

                    if (!initialConversations.length) {
                        selectedConversations = [];
                    } else {
                        //  this is a little bit of a hack to get the label
                        // the same for how it is in the slack-api-powered dropdown
                        const getConversationLabel = (conversation) => {
                            let label = conversation.name;
                            if (isChannel(conversation)) {
                                label = getChannelTitle(conversation);
                            } else if (isPrivateChannel(conversation)) {
                                label = getPrivateChannelTitle(conversation);
                            } else if (isDMChannel(conversation)) {
                                label = getDMChannelTitle(conversation);
                            }
                            return label;
                        };

                        selectedConversations = initialConversations.map(conversation => ({ label: getConversationLabel(conversation), value: conversation }));
                        if (this.props.onConversationChange) {
                            this.props.onConversationChange(selectedConversations);
                        }
                    }
                } else {
                    selectedConversations = [];
                }

                this.setState({ slackConversations, selectedConversations, loading });
            }).catch(console.error);

        return null;
    };

    getConversationOptions = () => {
        const { slackConversations } = this.state;
        if (!slackConversations || !slackConversations.length) {
            return null;
        }

        const conversationMap = {
            channels: {
                title: 'Channels',
                channels: [],
            },
            privateChannels: {
                title: 'Private Channels',
                channels: [],
            },
            dmChannels: {
                title: 'Direct Messages',
                channels: [],
            },
            groupDMChannels: {
                title: 'Group Messages',
                channels: [],
            },
        };
        slackConversations.forEach(channel => {
            for (const key in conversationMap) {
                const channelTest = CONVERSATION_METHODS[conversationMap[key].title].test;
                if (channelTest(channel)) {
                    const label = CONVERSATION_METHODS[conversationMap[key].title].getConversationTitle(channel);
                    const value = channel;
                    conversationMap[key].channels.push({ label, value });
                    break;
                }
            }
        });

        const tmpmap = Object.entries(conversationMap).map(entry => {
            const label = entry[1].title;
            const options = entry[1].channels;
            return { label, options };
        });

        return tmpmap;
    };

    onConversationChange = (selectedConversations) => {
        if (this.props.onConversationChange) {
            this.props.onConversationChange(selectedConversations);
        }

        if (selectedConversations && !Array.isArray(selectedConversations)) {
            this.setState({ selectedConversations: [selectedConversations] });
        } else {
            this.setState({ selectedConversations });
        }
    };

    render() {
        const { loading, selectedConversations, slackConversations } = this.state;
        const conversationOptions = this.getConversationOptions();

        const selectedConversationValues = selectedConversations ? selectedConversations.map(conversation => conversation.value) : [];

        const inputValue = this.props.isMulti
            ? JSON.stringify(selectedConversationValues)
            : selectedConversationValues.length
                ? JSON.stringify(selectedConversationValues[0])
                : '';

        return (
            <Fragment>
                <input
                    name="slack_conversation"
                    type="hidden"
                    id="slack_conversation"
                    value={inputValue}
                />

                {loading
                    ? (<div className="loading gray">Loading Slack conversations</div>)
                    : (
                        <Select
                            id="slack-channel-list"
                            className="bold block col-12 mb1"
                            placeholder="Select Slack conversation..."
                            value={selectedConversations}
                            onChange={this.onConversationChange}
                            options={conversationOptions}
                            isClearable
                            isMulti={this.props.isMulti}
                        />
                    )}
            </Fragment>
        );
    }
}

export default SlackConversationSelector;
